import { useEffect, useState } from "react";
import operatorApiService from "../../../../services/operator-api.service";
import "./LayoutModal.scss";
import videoIcon from "../../../../assets/imgs/master/video_icon_bk.svg";

interface Vehicle {
  vehicle_id: number;
  vehicle_label: string;
  checked?: boolean;
}

interface LayoutEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  monitorConsoleNo: string;
  baseId: number;
  initialLayout: (number | null)[];
}

const LayoutEditModal = ({
  isOpen,
  onClose,
  monitorConsoleNo,
  baseId,
  initialLayout,
}: LayoutEditModalProps) => {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [layout, setLayout] = useState<(number | null)[]>([
    null,
    null,
    null,
    null,
  ]);
  const [monitorVehicleIds, setMonitorVehicleIds] = useState<Set<number>>(
    new Set()
  );
  const [vehicleList, setVehicleList] = useState<Vehicle[]>([]);

  useEffect(() => {
    const fetchVehicles = async () => {
      const res = await operatorApiService.doGetMonitorVehiclesByConsoleNo({
        baseId,
        monitorConsoleNo,
      });

      const ids = res
        .filter((v: Vehicle) => v.checked)
        .map((v: any) => v.vehicle_id);
      setVehicleList(res);
      setMonitorVehicleIds(
        new Set([
          ...ids,
          ...(initialLayout.filter((v) => v != null) as number[]),
        ])
      );
    };

    if (isOpen) {
      setLayout(initialLayout);
      fetchVehicles();
    }
  }, [isOpen, initialLayout, monitorConsoleNo, baseId]);

  const handleSelectChange = (slot: number, value: string) => {
    const newLayout = [...layout];
    const newVehicleId = value ? parseInt(value) : null;
    newLayout[slot] = newVehicleId;
    setLayout(newLayout);

    if (newVehicleId != null) {
      setMonitorVehicleIds((prev) => new Set(prev).add(newVehicleId));
    }
  };

  const handleCheckboxChange = (vehicleId: number, checked: boolean) => {
    setMonitorVehicleIds((prev) => {
      const newSet = new Set(prev);
      if (checked) newSet.add(vehicleId);
      else newSet.delete(vehicleId);
      return newSet;
    });
  };

  const handleSubmit = async () => {
    try {
      const postdata = layout.map((vehicle_id, idx) => ({
        layout_id: idx + 1,
        vehicle_id: vehicle_id ?? undefined,
      }));

      const monitorVehicles = Array.from(monitorVehicleIds).map(
        (vehicle_id) => ({
          vehicle_id,
        })
      );

      await operatorApiService
        .saveFourVehicleLayout({
          base_id: baseId,
          monitor_console_no: monitorConsoleNo,
          postdata,
          monitorVehicles,
        })
        .then((response) => {
          if (response.status === "success") {
            setSuccessMessage("更新に成功しました。");
            setTimeout(() => {
              onClose();
              setSuccessMessage(null);
            }, 1000);
          }
        });
    } catch (err) {
      setErrorMessage("更新に失敗しました。");
      setTimeout(() => setErrorMessage(null), 3000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="layoutModal">
      <div className="modalOverlay">
        <div className="modalContent">
          <div className="modalTitle">監視卓 {monitorConsoleNo}</div>

          <div className="fourGrid">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="gridBox">
                <img src={videoIcon} alt="" />
                <div className="slotTitle">映像{i + 1}</div>
                <select
                  value={layout[i] !== null ? layout[i].toString() : ""}
                  onChange={(e) => handleSelectChange(i, e.target.value)}
                >
                  <option value="">未選択</option>
                  {vehicleList.map((v) => (
                    <option key={v.vehicle_id} value={v.vehicle_id.toString()}>
                      {v.vehicle_label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="modalTitle">監視対象車両</div>
          <ul className="checkboxList">
            {vehicleList.map((v) => {
              const selectedInLayout = layout.includes(v.vehicle_id);
              return (
                <li key={v.vehicle_id}>
                  <input
                    type="checkbox"
                    id={`chk_${v.vehicle_id}`}
                    checked={monitorVehicleIds.has(v.vehicle_id)}
                    disabled={selectedInLayout}
                    onChange={(e) =>
                      handleCheckboxChange(v.vehicle_id, e.target.checked)
                    }
                  />
                  <label htmlFor={`chk_${v.vehicle_id}`}>
                    {v.vehicle_label}
                  </label>
                </li>
              );
            })}
          </ul>
          {successMessage && (
            <div className="successLayoutMessage">{successMessage}</div>
          )}
          {errorMessage && (
            <div className="errorLayoutMessage">{errorMessage}</div>
          )}

          <div className="modalFooter">
            <button className="backModalButton" onClick={onClose}>
              戻る
            </button>
            <button className="saveButton" onClick={handleSubmit}>
              登録
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LayoutEditModal;
